import { TLLineShape, TLShape, TLShapeId, TLParentId, IndexKey, TLEditorSnapshot } from '@tldraw/tldraw';

import { VisualChronologyNodeShape } from '../components/TimelineElements/VisualChronologyNode';
import { DISTANCE_FROM_TIMELINE, FIRST_NODE_X, NODE_WIDTH, TIMELINE_Y } from '../constants';
import { defaultHeaders } from '../constants';
import { calculateNodeHeight } from '../timelineDrawingHelpers';
import {
  convertFactToNodeData,
  calculateNodePosition,
  createConnectingLineProps,
  calculateTimelineLength,
  createNodeShapeProps,
  findInsertionPosition,
  shiftNodesAfterInsertion,
  createInBetweenButtonProps,
  updateAddNewNodeButtonPosition,
  NodeData,
} from '../timelineSharedUtils';
import { APIBaseChronos } from '@/api/hosts';
import { FetchMethod } from '@/api/useGetFetchConfig';
import { ChronologyFact } from '@/types';

interface VisualisationSyncParams {
  matterId: string;
  subsetId: string;
  chronologyId: string;
}

// ============================================================================
// API Helper Functions
// ============================================================================

const getVisualisationSnapshot = async (
  matterId: string,
  subsetId: string,
  chronologyId: string,
  getAccessToken: () => Promise<string | undefined>,
) => {
  try {
    const authToken = await getAccessToken();
    if (!authToken) {
      throw new Error('No access token to fetch visualisation snapshot');
    }

    const response = await fetch(
      `${APIBaseChronos}/api/matter/${matterId}/subset/${subsetId}/chronology/${chronologyId}/visualisation`,
      {
        method: FetchMethod.GET,
        ...defaultHeaders(authToken),
      },
    );

    if (response.status === 404 || !response.ok) {
      return null;
    }

    return response.json();
  } catch (error) {
    console.error('Error fetching visualisation:', error);
    return null;
  }
};

const saveVisualisationSnapshot = async (
  matterId: string,
  subsetId: string,
  chronologyId: string,
  snapshot: TLEditorSnapshot,
  getAccessToken: () => Promise<string | undefined>,
) => {
  try {
    const authToken = await getAccessToken();
    if (!authToken) {
      throw new Error('No access token to save visualisation snapshot');
    }

    const response = await fetch(
      `${APIBaseChronos}/api/matter/${matterId}/subset/${subsetId}/chronology/${chronologyId}/save-visualisation`,
      {
        method: FetchMethod.POST,
        ...defaultHeaders(authToken),
        body: JSON.stringify({
          snapshot,
        }),
      },
    );

    if (!response.ok) {
      throw new Error('Failed to save visualisation');
    }

    return true;
  } catch (error) {
    console.error('Error saving visualisation:', error);
    return false;
  }
};

// ============================================================================
// Store Helper Functions
// ============================================================================

const getChronologyNodes = (store: Record<string, TLShape>): VisualChronologyNodeShape[] => {
  return Object.values(store)
    .filter((shape: TLShape) => shape.type === 'visual-chronology-node')
    .sort((a, b) => a.x - b.x) as VisualChronologyNodeShape[];
};

const removeNodeFromStore = (store: Record<string, TLShape>, nodeId: string) => {
  const nodeToDeleteId = `shape:visual-chronology-node-${nodeId}`;
  const connectingLineId = `shape:connecting-line-${nodeId}`;

  delete store[nodeToDeleteId];
  if (store[connectingLineId]) {
    delete store[connectingLineId];
  }
};

const removeTimelineElements = (store: Record<string, TLShape>, elementTypes: string[]) => {
  const allShapes = Object.keys(store);
  const elementsToRemove = allShapes.filter((key) => elementTypes.includes(store[key].type));

  elementsToRemove.forEach((key) => {
    delete store[key];
  });
};

const clearAllExceptBasicTimeline = (store: Record<string, TLShape>) => {
  const keysToDelete = Object.keys(store).filter(
    (key) => !['shape:timeline-line', 'shape:add-new-node-button', 'page:page', 'document:document'].includes(key),
  );
  keysToDelete.forEach((key) => delete store[key]);
};

// ============================================================================
// Node Creation Helper Functions
// ============================================================================

const createNodeShape = (
  nodeData: NodeData,
  nodeX: number,
  nodeY: number,
  calculatedHeight: number,
  isAbove: boolean,
  nodeId: string,
): VisualChronologyNodeShape => {
  const newNodeId = `shape:visual-chronology-node-${nodeId}`;

  return {
    x: nodeX,
    y: nodeY,
    rotation: 0,
    isLocked: false,
    opacity: 1,
    meta: {},
    id: newNodeId as TLShapeId,
    type: 'visual-chronology-node',
    props: createNodeShapeProps(nodeData, calculatedHeight, isAbove),
    parentId: 'page:page' as TLParentId,
    typeName: 'shape',
    index: `a${Date.now() + 1}` as IndexKey,
  } as VisualChronologyNodeShape;
};

const createConnectingLine = (nodeX: number, isAbove: boolean, nodeId: string): TLShape => {
  const connectingLineId = `shape:connecting-line-${nodeId}`;

  return {
    x: nodeX + NODE_WIDTH / 2,
    y: TIMELINE_Y,
    rotation: 0,
    isLocked: true,
    opacity: 1,
    meta: {},
    id: connectingLineId as TLShapeId,
    type: 'line',
    props: createConnectingLineProps(isAbove),
    parentId: 'page:page' as TLParentId,
    index: `a${Date.now() + 1}` as IndexKey,
    typeName: 'shape',
  };
};

// ============================================================================
// Timeline Update Helper Functions
// ============================================================================

const updateTimelineLine = (store: Record<string, TLShape>, nodes: VisualChronologyNodeShape[]) => {
  const timelineLine = store['shape:timeline-line'] as TLLineShape;
  if (timelineLine) {
    const rightmostX = calculateTimelineLength(nodes);
    timelineLine.props.points['1'].x = rightmostX;
  }
};

const resetTimelineLine = (store: Record<string, TLShape>) => {
  const timelineLine = store['shape:timeline-line'] as TLLineShape;
  if (timelineLine) {
    timelineLine.props.points['1'].x = FIRST_NODE_X;
  }
};

const resetAddNewNodeButton = (store: Record<string, TLShape>) => {
  const addNewNodeButton = store['shape:add-new-node-button'];
  if (addNewNodeButton) {
    addNewNodeButton.x = FIRST_NODE_X + 70;
  }
};

const recreateInBetweenButtons = (store: Record<string, TLShape>, nodes: VisualChronologyNodeShape[]) => {
  for (let i = 0; i < nodes.length - 1; i++) {
    const inBetweenButtonId = `shape:${Date.now() + i}-in-between`;
    const nodeX1 = nodes[i].x;
    const nodeX2 = nodes[i + 1].x;
    const centerX = (nodeX1 + NODE_WIDTH + nodeX2) / 2;

    store[inBetweenButtonId] = {
      ...createInBetweenButtonProps(i, centerX),
      id: inBetweenButtonId,
    } as TLShape;
  }
};

const recreateYearMarkers = (store: Record<string, TLShape>, nodes: VisualChronologyNodeShape[]) => {
  let currentYear: string | null = null;

  nodes.forEach((node) => {
    if (node.props.date) {
      const factYear = new Date(node.props.date).getFullYear().toString();
      if (factYear !== currentYear) {
        currentYear = factYear;

        const yearMarkerId = `shape:year-marker-${factYear}-${Date.now()}`;
        store[yearMarkerId] = {
          type: 'year-marker',
          x: node.x + 50,
          y: TIMELINE_Y - 20,
          rotation: 0,
          isLocked: true,
          opacity: 1,
          meta: {},
          id: yearMarkerId as TLShapeId,
          props: {
            w: 50,
            h: 40,
            year: factYear,
          },
          parentId: 'page:page' as TLParentId,
          index: `a${Date.now() + 200}` as IndexKey,
          typeName: 'shape',
        };
      }
    }
  });
};

const repositionRemainingNodes = (store: Record<string, TLShape>, nodes: VisualChronologyNodeShape[]) => {
  nodes.forEach((node: VisualChronologyNodeShape, index: number) => {
    const { nodeX: newX, nodeY: newY, isAbove: newIsAbove } = calculateNodePosition(index, node.props.h);

    // Update node position and isAbove property
    node.x = newX;
    node.y = newY;
    node.props.isAbove = newIsAbove;

    // Update corresponding connecting line
    const nodeId = node.props.nodeId;
    const lineId = `shape:connecting-line-${nodeId}`;
    const line = store[lineId] as TLLineShape;
    if (line) {
      line.x = newX + NODE_WIDTH / 2;
      line.y = TIMELINE_Y;
      const connectingLineLength = newIsAbove ? -DISTANCE_FROM_TIMELINE : DISTANCE_FROM_TIMELINE;
      line.props.points['1'].y = connectingLineLength;
    }
  });
};

const removeOrphanedElements = (store: Record<string, TLShape>, rightmostNodeX: number) => {
  const elementsToCheck = Object.keys(store).filter(
    (key) => store[key].type === 'add-in-between-node-button' || store[key].type === 'year-marker',
  );

  elementsToCheck.forEach((key) => {
    const element = store[key];
    if (element.x > rightmostNodeX + NODE_WIDTH / 2) {
      delete store[key];
    }
  });
};

// ============================================================================
// Main Operations for syncing the visualisation with timeline changes
// ============================================================================

// Add a new fact to the visual timeline
const addFactToVisualisation = async (
  params: VisualisationSyncParams & { newFact: ChronologyFact; getAccessToken: () => Promise<string | undefined> },
) => {
  const { matterId, subsetId, chronologyId, newFact, getAccessToken } = params;

  try {
    // Get existing snapshot
    const existingSnapshot = await getVisualisationSnapshot(matterId, subsetId, chronologyId, getAccessToken);

    if (!existingSnapshot || !existingSnapshot.snapshot) {
      // No visualisation exists, nothing to add to
      return;
    }

    const store: Record<string, TLShape> = existingSnapshot.snapshot.document.store;
    const nodeData = convertFactToNodeData(newFact);

    // Get all existing chronology nodes, preserving current order of nodes in the visual timeline
    const existingNodes = getChronologyNodes(store);

    // Find the correct insertion position based on date
    const insertionIndex = findInsertionPosition(existingNodes, nodeData.date);

    // Calculate position for new node
    const calculatedHeight = calculateNodeHeight(nodeData.description, nodeData.documentNames);
    const { nodeX, nodeY, isAbove } = calculateNodePosition(insertionIndex, calculatedHeight);

    // Shift all nodes at or after the insertion point to the right
    const nodesToShift = existingNodes.slice(insertionIndex);
    shiftNodesAfterInsertion(nodesToShift, insertionIndex, store);

    // Create new node shape
    const newNodeShape = createNodeShape(nodeData, nodeX, nodeY, calculatedHeight, isAbove, nodeData.nodeId);
    store[newNodeShape.id] = newNodeShape;

    // Create connecting line
    const connectingLine = createConnectingLine(nodeX, isAbove, nodeData.nodeId);
    store[connectingLine.id] = connectingLine;

    // Remove  all in-between buttons and year markers
    removeTimelineElements(store, ['add-in-between-node-button', 'year-marker']);

    // Get all nodes sorted by position now (including the new one)
    const allNodes = getChronologyNodes(store);

    // Recreate in-between buttons
    recreateInBetweenButtons(store, allNodes);

    // Recreate year markers
    recreateYearMarkers(store, allNodes);

    // Update timeline line length
    updateTimelineLine(store, allNodes);

    // Update add new node button position
    updateAddNewNodeButtonPosition(allNodes, store);

    // Save the updated snapshot
    await saveVisualisationSnapshot(
      matterId,
      subsetId,
      chronologyId,
      {
        document: existingSnapshot.snapshot.document,
        session: existingSnapshot.snapshot.session,
      },
      getAccessToken,
    );

    return true;
  } catch (error) {
    console.error('Error adding fact to timeline:', error);
    return false;
  }
};

// Edit an existing fact in the visual timeline
const editFactInVisualisation = async (
  params: VisualisationSyncParams & { updatedFact: ChronologyFact; getAccessToken: () => Promise<string | undefined> },
) => {
  const { matterId, subsetId, chronologyId, updatedFact, getAccessToken } = params;

  try {
    // Get existing snapshot
    const existingSnapshot = await getVisualisationSnapshot(matterId, subsetId, chronologyId, getAccessToken);

    if (!existingSnapshot || !existingSnapshot.snapshot) {
      // No visualisation exists, do nothing
      return;
    }

    const store = existingSnapshot.snapshot.document.store as Record<string, TLShape>;
    const nodeId = updatedFact.id;
    const node = store[`shape:visual-chronology-node-${nodeId}`];

    node.props = {
      ...node.props,
      description: updatedFact.description,
      date: updatedFact.factDate,
      dateText: updatedFact.factDateText,
    };

    // Remove existing year markers and recreate them in case the date change affects year boundaries
    removeTimelineElements(store, ['year-marker']);

    // Get all nodes and recreate year markers
    const allNodes = getChronologyNodes(store);
    recreateYearMarkers(store, allNodes);

    await saveVisualisationSnapshot(
      matterId,
      subsetId,
      chronologyId,
      {
        document: existingSnapshot.snapshot.document,
        session: existingSnapshot.snapshot.session,
      },
      getAccessToken,
    );
  } catch (error) {
    console.error('Error editing fact in timeline:', error);
    return false;
  }
};

// Delete a fact from the visual timeline
const deleteFactFromVisualisation = async (
  params: VisualisationSyncParams & { nodeId: string; getAccessToken: () => Promise<string | undefined> },
) => {
  const { matterId, subsetId, chronologyId, nodeId, getAccessToken } = params;

  try {
    // Get existing snapshot
    const existingSnapshot = await getVisualisationSnapshot(matterId, subsetId, chronologyId, getAccessToken);

    if (!existingSnapshot || !existingSnapshot.snapshot) {
      // No visualisation exists, nothing to delete
      return true;
    }

    const store = existingSnapshot.snapshot.document.store as Record<string, TLShape>;
    const nodeToDeleteId = `shape:visual-chronology-node-${nodeId}`;

    // Check if the node exists
    if (!store[nodeToDeleteId]) {
      // Node doesn't exist, nothing to delete
      return true;
    }

    // Remove the node and its connecting line
    removeNodeFromStore(store, nodeId);

    // Get remaining nodes and sort by x position
    const remainingNodes = getChronologyNodes(store);

    if (remainingNodes.length === 0) {
      // No nodes left, clear everything except basic timeline
      clearAllExceptBasicTimeline(store);

      // Reset timeline line to minimal length
      resetTimelineLine(store);

      // Reset add new node button position
      resetAddNewNodeButton(store);
    } else {
      // Reposition remaining nodes and update their alternating pattern
      repositionRemainingNodes(store, remainingNodes);

      // Remove any orphaned in-between buttons and year markers
      const rightmostNodeX = Math.max(...remainingNodes.map((node) => node.x));
      removeOrphanedElements(store, rightmostNodeX);

      // Recreate year markers for remaining nodes
      recreateYearMarkers(store, remainingNodes);

      // Update timeline line length
      updateTimelineLine(store, remainingNodes);

      // Update add new node button position
      updateAddNewNodeButtonPosition(remainingNodes, store);
    }

    // Save the updated snapshot
    await saveVisualisationSnapshot(
      matterId,
      subsetId,
      chronologyId,
      {
        document: existingSnapshot.snapshot.document,
        session: existingSnapshot.snapshot.session,
      },
      getAccessToken,
    );

    return true;
  } catch (error) {
    console.error('Error deleting fact from timeline:', error);
    return false;
  }
};

// Add multiple facts to the visual timeline in a single batch operation
const addFactsBatchToVisualisation = async (
  params: VisualisationSyncParams & {
    newFacts: ChronologyFact[];
    getAccessToken: () => Promise<string | undefined>;
  },
) => {
  const { matterId, subsetId, chronologyId, newFacts, getAccessToken } = params;

  if (!newFacts || newFacts.length === 0) {
    return true;
  }

  try {
    // Get existing snapshot
    const existingSnapshot = await getVisualisationSnapshot(matterId, subsetId, chronologyId, getAccessToken);

    if (!existingSnapshot || !existingSnapshot.snapshot) {
      // No visualisation exists, nothing to add to
      return;
    }

    const store: Record<string, TLShape> = existingSnapshot.snapshot.document.store;

    // Get all existing chronology nodes, preserving current order
    const existingNodes = getChronologyNodes(store);

    // Convert all facts to node data and sort by date
    const newNodeDataList = newFacts
      .map((fact) => convertFactToNodeData(fact))
      .sort((a, b) => {
        const dateA = a.date ? new Date(a.date).getTime() : 0;
        const dateB = b.date ? new Date(b.date).getTime() : 0;
        return dateA - dateB;
      });

    // Process each fact and add to the timeline
    let currentNodes = [...existingNodes];

    for (const nodeData of newNodeDataList) {
      // Find the correct insertion position based on date
      const insertionIndex = findInsertionPosition(currentNodes, nodeData.date);

      // Calculate position for new node
      const calculatedHeight = calculateNodeHeight(nodeData.description, nodeData.documentNames);
      const { nodeX, nodeY, isAbove } = calculateNodePosition(insertionIndex, calculatedHeight);

      // Shift all nodes at or after the insertion point to the right
      const nodesToShift = currentNodes.slice(insertionIndex);
      shiftNodesAfterInsertion(nodesToShift, insertionIndex, store);

      // Create new node shape
      const newNodeShape = createNodeShape(nodeData, nodeX, nodeY, calculatedHeight, isAbove, nodeData.nodeId);
      store[newNodeShape.id] = newNodeShape;

      // Create connecting line
      const connectingLine = createConnectingLine(nodeX, isAbove, nodeData.nodeId);
      store[connectingLine.id] = connectingLine;

      // Update the current nodes array to include the new node for next iteration
      currentNodes.splice(insertionIndex, 0, newNodeShape);
    }

    // Remove all in-between buttons and year markers
    removeTimelineElements(store, ['add-in-between-node-button', 'year-marker']);

    // Get all nodes sorted by position now (including all new ones)
    const allNodes = getChronologyNodes(store);

    // Recreate in-between buttons
    recreateInBetweenButtons(store, allNodes);

    // Recreate year markers
    recreateYearMarkers(store, allNodes);

    // Update timeline line length
    updateTimelineLine(store, allNodes);

    // Update add new node button position
    updateAddNewNodeButtonPosition(allNodes, store);

    // Save the updated snapshot once
    await saveVisualisationSnapshot(
      matterId,
      subsetId,
      chronologyId,
      {
        document: existingSnapshot.snapshot.document,
        session: existingSnapshot.snapshot.session,
      },
      getAccessToken,
    );

    return true;
  } catch (error) {
    console.error('Error adding facts batch to timeline:', error);
    return false;
  }
};

export { addFactToVisualisation, addFactsBatchToVisualisation, editFactInVisualisation, deleteFactFromVisualisation };
